import React, { useEffect, useState, useContext } from 'react';
import {
  Typography, Table, Button, Modal, Form, Input, Switch, Select, Space, Popconfirm, message, Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

export default function CampaignsAdminPage() {
  const currentUser = useContext(UserContext);
  const canEdit     = currentUser?.Role === 'admin';
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form] = Form.useForm();
  const [teams, setTeams] = useState([]);
  const [jobs, setJobs]   = useState([]);

  const load = () => {
    setLoading(true);
    api.get('/api/admin/campaigns')
      .then((r) => setCampaigns(r.data))
      .catch(() => message.error('Failed to load campaigns.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/api/admin/teams').then((r) => setTeams(r.data || [])).catch(() => {});
    api.get('/api/jobs').then((r) => setJobs(r.data || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditItem(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, eval_source: 'cxone', ai_data: false });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditItem(record);
    form.setFieldsValue({
      name:           record.Name,
      cx_campaign_no: record.CXCampaignNo ?? '',
      eval_source:    record.EvalSource ?? 'cxone',
      ai_data:        !!record.AIData,
      is_active:      !!record.IsActive,
      default_team_id: record.DefaultTeamID ?? undefined,
      default_job_id:  record.DefaultJobID ?? undefined,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/api/admin/campaigns/${editItem.ID}`, {
          name:           values.name,
          cx_campaign_no: values.cx_campaign_no || null,
          eval_source:    values.eval_source,
          ai_data:        values.ai_data,
          is_active:      values.is_active,
          default_team_id: values.default_team_id ?? null,
          default_job_id:  values.default_job_id ?? null,
        });
        message.success('Campaign updated.');
      } else {
        await api.post('/api/admin/campaigns', {
          name:           values.name,
          cx_campaign_no: values.cx_campaign_no || null,
          eval_source:    values.eval_source,
          ai_data:        values.ai_data,
          default_team_id: values.default_team_id ?? null,
          default_job_id:  values.default_job_id ?? null,
        });
        message.success('Campaign created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save campaign.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/campaigns/${id}`);
      message.success('Campaign deleted.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to delete campaign.');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 60 },
    {
      title: 'Campaign Name',
      dataIndex: 'Name',
      key: 'Name',
      sorter: (a, b) => a.Name.localeCompare(b.Name),
    },
    {
      title: 'External Campaign ID',
      dataIndex: 'CXCampaignNo',
      key: 'CXCampaignNo',
      width: 150,
      render: (v) => v ?? <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Eval Source',
      dataIndex: 'EvalSource',
      key: 'EvalSource',
      width: 120,
      render: (v) => (
        <Tag color={v === 'qa_app' ? 'blue' : 'purple'}>
          {v === 'qa_app' ? 'QA App' : 'CXOne'}
        </Tag>
      ),
    },
    {
      // Whether this campaign's calls go through the AI. What the AI Insights
      // tabs on the Campaign Summary are offered on, since they read tables
      // that only hold rows for scored campaigns.
      title: 'AI Data',
      dataIndex: 'AIData',
      key: 'AIData',
      width: 100,
      render: (v) => v
        ? <Tag color="geekblue">AI</Tag>
        : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Default Team',
      dataIndex: 'DefaultTeamName',
      key: 'DefaultTeamName',
      width: 140,
      render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Default Job',
      dataIndex: 'DefaultJobName',
      key: 'DefaultJobName',
      width: 140,
      render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'IsActive',
      key: 'IsActive',
      width: 100,
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    ...(canEdit ? [{
      title: '',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this campaign?"
            description="Users assigned to this campaign will keep their assignment but it won't appear in new dropdowns."
            onConfirm={() => handleDelete(record.ID)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Campaign Management
        </Typography.Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Campaign
          </Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={campaigns}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} campaigns` }}
      />

      <Modal maskClosable={false}
        title={editItem ? `Edit — ${editItem.Name}` : 'New Campaign'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
        width={440}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Campaign Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. BGE" />
          </Form.Item>
          <Form.Item
            label="External Campaign ID"
            name="cx_campaign_no"
            tooltip="The campaign_no value from Superior/CXOne. Used to robustly link call log data even if the name drifts."
          >
            <Input placeholder="e.g. 12345" />
          </Form.Item>
          <Form.Item
            label="Eval Source"
            name="eval_source"
            tooltip="Which system provides evaluation scores for the leaderboard. CXOne = QMWorkFlow. QA App = QACases."
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { label: 'CXOne', value: 'cxone' },
                { label: 'QA App', value: 'qa_app' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="AI Data"
            name="ai_data"
            valuePropName="checked"
            tooltip="This campaign's calls are scored by the AI. Turns on the AI Insights tabs on the Campaign Summary, which read tables that only hold rows for scored campaigns."
          >
            <Switch />
          </Form.Item>
          <Form.Item label="Default Team" name="default_team_id">
            <Select allowClear showSearch optionFilterProp="label" placeholder="None"
              options={teams.map((t) => ({ value: t.ID, label: t.Name }))} />
          </Form.Item>
          <Form.Item label="Default Job" name="default_job_id">
            <Select allowClear showSearch optionFilterProp="label" placeholder="None"
              options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))} />
          </Form.Item>
          {editItem && (
            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}
